import { motion } from "framer-motion";
import { ArrowRight, Gauge, Rocket, RotateCw, Target, Zap } from "lucide-react";
import { useOutletContext } from "react-router-dom";

const steps = [
  {
    title: "Acquire",
    icon: Target,
    summary: "Acquire Users Predictably",
    intro: "We build acquisition systems that bring the right users at the right cost.",
    items: [
      "Paid acquisition (Meta, Google, UAC)",
      "Influencer-led growth campaigns",
      "Creative testing systems",
      "Audience targeting optimization",
    ],
    outcome: "High-quality installs with controlled CAC",
  },
  {
    title: "Convert",
    icon: Zap,
    summary: "Turn Installs into Active Users",
    intro: "Traffic without conversion is wasted spend. We optimize every touchpoint to increase activation.",
    items: [
      "App Store Optimization (ASO)",
      "Store listing conversion (CVR boost)",
      "Landing page optimization",
      "Onboarding flow improvements",
    ],
    outcome: "+30–60% increase in install-to-user conversion",
  },
  {
    title: "Retain",
    icon: RotateCw,
    summary: "Build Trust & Keep Users Engaged",
    intro: "Retention is where real growth happens. We engineer systems that keep users coming back.",
    items: [
      "Review & rating systems (4.5⭐+ maintenance)",
      "Negative feedback control",
      "Engagement optimization",
      "Lifecycle improvements",
    ],
    outcome: "Higher retention, stronger brand trust, better LTV",
  },
  {
    title: "Scale",
    icon: Rocket,
    summary: "Scale What Works, Eliminate What Doesn’t",
    intro: "Once the system works — we scale it aggressively and efficiently.",
    items: [
      "Performance optimization loops",
      "Budget scaling strategies",
      "Funnel-level data tracking",
      "AI-driven automation systems",
    ],
    outcome: "Sustainable growth without burning budget",
  },
];

export default function HowWeScaleAppsPage() {
  const { onPrimaryAction } = useOutletContext();

  return (
    <section className="px-6 pb-16 pt-14 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-4xl">
          <p className="text-xs uppercase tracking-[0.3em] text-white/[0.42]">How We Scale Apps</p>
          <h1 className="mt-4 font-display text-4xl text-white sm:text-5xl lg:text-6xl">How We Scale Apps</h1>
          <p className="mt-5 text-xl leading-8 text-white/[0.8]">We don’t rely on hacks or campaigns. We build systems that compound growth.</p>
          <p className="mt-5 max-w-3xl text-base leading-8 text-white/[0.64]">
            AI-powered acquisition, conversion, and retention frameworks designed to scale apps predictably.
          </p>
          <button
            type="button"
            onClick={onPrimaryAction}
            className="glass-button glass-panel mt-8 rounded-full px-7 py-4 text-sm font-medium text-white transition-all duration-300 hover:scale-[1.03]"
            data-testid="scale-apps-hero-cta"
          >
            <span className="relative z-10 inline-flex items-center gap-2">
              Get Your Growth Blueprint →
              <ArrowRight className="h-4 w-4" />
            </span>
          </button>
        </motion.div>

        <section className="mt-16 glitter-card glass-panel rounded-[2rem] p-6 lg:p-8" data-testid="scale-system-overview">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-white/[0.42]">
            <Gauge className="h-4 w-4 text-cyan-300" />
            The System Overview
          </div>
          <h2 className="mt-4 font-display text-3xl text-white sm:text-4xl">A 4-Step Growth System</h2>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-white/[0.64]">From first click to long-term retention — every stage is engineered.</p>
          <div className="workflow-nodes mt-8">
            {steps.map((step, index) => (
              <div key={step.title} className={`workflow-node ${index === 0 ? "active" : ""}`} data-testid={`scale-step-pill-${index + 1}`}>
                <span className="workflow-node-dot" />
                <span className="workflow-node-label inline-flex items-center gap-2"><step.icon className="h-4 w-4 text-cyan-300" />{step.title}</span>
                {index < steps.length - 1 ? <span className="workflow-connector" /> : null}
              </div>
            ))}
          </div>
        </section>

        <div className="mt-10 grid gap-5 lg:grid-cols-2">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.article
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-120px" }}
                transition={{ duration: 0.55, delay: index * 0.08 }}
                className="glitter-card glass-panel cinematic-card rounded-[2rem] p-6 lg:p-7"
                data-testid={`scale-step-card-${index + 1}`}
              >
                <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-white/[0.12] bg-gradient-to-br from-violet-500/20 to-cyan-500/20 text-white shadow-[0_0_28px_rgba(139,92,246,0.15)]">
                  <Icon className="h-6 w-6" />
                </div>
                <p className="text-xs uppercase tracking-[0.24em] text-white/[0.42]">Step {index + 1}</p>
                <h3 className="mt-3 font-display text-3xl text-white">{step.summary}</h3>
                <p className="mt-4 text-sm leading-7 text-white/[0.66]">{step.intro}</p>
                <div className="mt-5 space-y-3 text-sm text-white/[0.72]">
                  {step.items.map((item) => (
                    <div key={item} className="rounded-[1.1rem] border border-white/[0.08] bg-black/20 px-4 py-3">
                      {item}
                    </div>
                  ))}
                </div>
                <div className="mt-6 rounded-[1.25rem] border border-white/[0.08] bg-white/[0.04] px-4 py-4 text-sm text-cyan-200">
                  📈 {step.outcome}
                </div>
              </motion.article>
            );
          })}
        </div>

        <section className="mt-12 glitter-card glass-panel rounded-[2rem] p-6 lg:p-8" data-testid="scale-why-this-works">
          <p className="text-xs uppercase tracking-[0.28em] text-white/[0.42]">Why This Works</p>
          <h2 className="mt-4 font-display text-3xl text-white sm:text-4xl">Most agencies run campaigns. We build systems that improve over time.</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {[
              "Better acquisition → higher conversion",
              "Better conversion → stronger retention",
              "Better retention → lower CAC → easier scaling",
            ].map((item) => (
              <div key={item} className="rounded-[1.25rem] border border-white/[0.08] bg-black/20 px-4 py-4 text-sm text-white/[0.72]">
                {item}
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 glitter-card glass-panel rounded-[2rem] p-6 lg:p-8" data-testid="scale-proof-strip">
          <div className="grid gap-4 md:grid-cols-4">
            {["70+ Apps Scaled", "3M+ Installs Driven", "4.6⭐ Average Rating Maintained", "CAC Reduced by 30–60%"].map((item, index) => (
              <div key={item} className="rounded-[1.3rem] border border-white/[0.08] bg-black/20 px-4 py-4 text-sm text-white/[0.76]" data-testid={`scale-proof-item-${index + 1}`}>
                {item}
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 glitter-card glass-panel rounded-[2rem] p-6 text-center lg:p-8" data-testid="scale-final-cta">
          <p className="text-xs uppercase tracking-[0.28em] text-white/[0.42]">Final CTA</p>
          <h2 className="mt-4 font-display text-3xl text-white sm:text-4xl">Ready to Build Your Growth Engine?</h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-white/[0.64]">
            Let’s break down your current growth bottlenecks and build a system that scales.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <button
              type="button"
              onClick={onPrimaryAction}
              className="glass-button glass-panel rounded-full px-6 py-4 text-sm font-medium text-white transition-all duration-300 hover:scale-[1.03]"
              data-testid="scale-final-audit-button"
            >
              <span className="relative z-10 inline-flex items-center gap-2">
                Audit My App →
                <ArrowRight className="h-4 w-4" />
              </span>
            </button>
            <button
              type="button"
              onClick={onPrimaryAction}
              className="glass-button glass-panel rounded-full px-6 py-4 text-sm font-medium text-white transition-all duration-300 hover:scale-[1.03]"
              data-testid="scale-final-call-button"
            >
              <span className="relative z-10 inline-flex items-center gap-2">
                Book a Growth Call
                <ArrowRight className="h-4 w-4" />
              </span>
            </button>
          </div>
        </section>
      </div>
    </section>
  );
}